import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Search, ShieldCheck, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { openapiAPI } from '../../utils/api';
import type { OpenapiSearchResultItem } from '../../types/openapi';
import { useOpenapiQuota } from '../../hooks/useOpenapiQuota';
import RequestOpenapiUpgradeModal, { UpgradeMode } from '../lead/RequestOpenapiUpgradeModal';
import type { CrmFormValues } from './crmFormSchema';

export default function CrmCompanyLookup() {
  const { setValue } = useFormContext<CrmFormValues>();
  const queryClient = useQueryClient();
  const quotaQuery = useOpenapiQuota(true);
  const featureEnabled = quotaQuery.data?.feature_enabled ?? false;
  const advancedRemaining = quotaQuery.data
    ? Math.max(quotaQuery.data.advanced.limit - quotaQuery.data.advanced.used, 0)
    : null;

  const [shortcutOpen, setShortcutOpen] = useState(false);
  const [shortcutPi, setShortcutPi] = useState('');
  const [searchResults, setSearchResults] = useState<OpenapiSearchResultItem[] | null>(
    null
  );
  const [upgradeMode, setUpgradeMode] = useState<UpgradeMode | null>(null);

  const handleOpenapiError = (error: any, lookupType: 'advanced' | 'visura' | 'base') => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 403 && detail === 'feature_disabled') {
      toast.error('Integrazione anagrafica imprese non attiva sul piano corrente');
      setUpgradeMode('feature');
      return;
    }
    if (status === 429 && detail === 'quota_exceeded') {
      toast.error('Quota esaurita');
      setUpgradeMode(lookupType === 'visura' ? 'quota_visura' : 'quota_advanced');
      return;
    }
    if (status === 503) {
      toast.error('Verifica quota non disponibile, riprova');
      return;
    }
    if (status === 502) {
      toast.error('Servizio temporaneamente non disponibile, riprova');
      return;
    }
    if (status === 400) {
      toast.error(typeof detail === 'string' ? detail : 'Partita IVA non valida');
      return;
    }
    toast.error(typeof detail === 'string' ? detail : 'Errore durante la chiamata anagrafica imprese');
  };

  const validateMutation = useMutation({
    mutationFn: () => openapiAPI.validatePi({ partita_iva: shortcutPi.trim() }),
    onSuccess: (res) => {
      const data = res.data;
      if (!data.valid) {
        toast.error('Partita IVA non valida');
        return;
      }
      setValue('tipo', 'azienda', { shouldDirty: true });
      setValue('partita_iva', shortcutPi.trim(), { shouldDirty: true });
      if (data.codice_fiscale) {
        setValue('codice_fiscale', data.codice_fiscale, { shouldDirty: true });
      }
      if (data.denominazione) {
        setValue('ragione_sociale', data.denominazione, { shouldDirty: true });
      }
      if (data.indirizzo_legale) {
        const ind = data.indirizzo_legale;
        if (ind.via) setValue('indirizzo', ind.via, { shouldDirty: true });
        if (ind.cap) setValue('cap', ind.cap, { shouldDirty: true });
        if (ind.citta) setValue('citta', ind.citta, { shouldDirty: true });
        if (ind.provincia) setValue('provincia', ind.provincia, { shouldDirty: true });
      }
      toast.success('P.IVA validata, dati precompilati');
      setShortcutOpen(false);
    },
    onError: (e) => handleOpenapiError(e, 'base'),
  });

  const searchMutation = useMutation({
    mutationFn: () =>
      openapiAPI.searchAdvanced({ partita_iva: shortcutPi.trim() }),
    onSuccess: (res) => {
      const data = res.data;
      queryClient.invalidateQueries({ queryKey: ['openapi', 'quota'] });
      if (data.count === 0 || data.results.length === 0) {
        toast.error('Nessun risultato per questa P.IVA');
        return;
      }
      if (data.results.length === 1) {
        applySearchResult(data.results[0]);
        toast.success('Dati anagrafici precompilati');
        return;
      }
      setSearchResults(data.results);
    },
    onError: (e) => handleOpenapiError(e, 'advanced'),
  });

  const applySearchResult = (item: OpenapiSearchResultItem) => {
    setValue('tipo', 'azienda', { shouldDirty: true });
    setValue('ragione_sociale', item.denominazione, { shouldDirty: true });
    setValue('partita_iva', item.partita_iva, { shouldDirty: true });
    if (item.codice_fiscale) {
      setValue('codice_fiscale', item.codice_fiscale, { shouldDirty: true });
    }
    if (item.pec) setValue('pec', item.pec, { shouldDirty: true });
    if (item.telefono) setValue('telefono', item.telefono, { shouldDirty: true });
    if (item.indirizzo) {
      const ind = item.indirizzo;
      if (ind.via) setValue('indirizzo', ind.via, { shouldDirty: true });
      if (ind.cap) setValue('cap', ind.cap, { shouldDirty: true });
      if (ind.citta) setValue('citta', ind.citta, { shouldDirty: true });
      if (ind.provincia) setValue('provincia', ind.provincia, { shouldDirty: true });
    }
    setSearchResults(null);
    setShortcutOpen(false);
  };

  // Pulsanti shortcut: stati gating
  const piTrim = shortcutPi.trim();
  const piLooksValid = /^\d{11}$/.test(piTrim);
  const advancedQuotaExhausted = featureEnabled && advancedRemaining === 0;

  const validateBtn = useMemo(() => {
    if (!featureEnabled) {
      return {
        disabled: true,
        title: 'Disponibile sui piani con integrazione anagrafica imprese attiva',
        badge: null as string | null,
      };
    }
    return {
      disabled: !piLooksValid || validateMutation.isPending,
      title: piLooksValid ? 'Valida formato + Agenzia Entrate' : 'Inserisci una P.IVA di 11 cifre',
      badge: null,
    };
  }, [featureEnabled, piLooksValid, validateMutation.isPending]);

  const searchBtn = useMemo(() => {
    if (!featureEnabled) {
      return {
        disabled: true,
        title: 'Disponibile sui piani con integrazione anagrafica imprese attiva',
        badge: null as string | null,
      };
    }
    if (advancedQuotaExhausted) {
      return {
        disabled: false,
        title: 'Quota giornaliera esaurita: clicca per richiedere upgrade',
        badge: 'Quota esaurita',
      };
    }
    return {
      disabled: !piLooksValid || searchMutation.isPending,
      title: piLooksValid
        ? `Cerca anagrafica completa (${advancedRemaining ?? '?'}/giorno rimanenti)`
        : 'Inserisci una P.IVA di 11 cifre',
      badge: null,
    };
  }, [
    featureEnabled,
    advancedQuotaExhausted,
    piLooksValid,
    searchMutation.isPending,
    advancedRemaining,
  ]);

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm';
  return <>
            {/* P.IVA shortcut */}
            <div className="border border-blue-200 bg-blue-50 rounded-lg">
              <button
                type="button"
                onClick={() => setShortcutOpen((s) => !s)}
                className="w-full flex items-center justify-between p-3 text-sm font-medium text-blue-900 hover:bg-blue-100 rounded-t-lg"
              >
                <span className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Hai una P.IVA? Compila in automatico
                </span>
                {shortcutOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>

              {shortcutOpen && (
                <div className="px-3 pb-3 space-y-3">
                  {!featureEnabled && !quotaQuery.isLoading && (
                    <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2 rounded">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        Integrazione anagrafica imprese non disponibile sul piano corrente.{' '}
                        <button
                          type="button"
                          className="underline font-medium"
                          onClick={() => setUpgradeMode('feature')}
                        >
                          Richiedi attivazione
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={11}
                      value={shortcutPi}
                      onChange={(e) => setShortcutPi(e.target.value.replace(/\D/g, ''))}
                      placeholder="P.IVA (11 cifre)"
                      className={inputClass + ' sm:flex-1'}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        title={validateBtn.title}
                        disabled={validateBtn.disabled}
                        onClick={() => validateMutation.mutate()}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                      >
                        {validateMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ShieldCheck className="h-4 w-4" />
                        )}
                        Solo validazione
                      </button>
                      <button
                        type="button"
                        title={searchBtn.title}
                        disabled={searchBtn.disabled}
                        onClick={() => {
                          if (advancedQuotaExhausted) {
                            setUpgradeMode('quota_advanced');
                            return;
                          }
                          searchMutation.mutate();
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap relative"
                      >
                        {searchMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Search className="h-4 w-4" />
                        )}
                        Ricerca avanzata
                        {searchBtn.badge && (
                          <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-amber-300 text-amber-900 rounded">
                            {searchBtn.badge}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>

                  {featureEnabled && advancedRemaining !== null && !advancedQuotaExhausted && (
                    <p className="text-xs text-blue-800">
                      Quota ricerca avanzata: {advancedRemaining}/
                      {quotaQuery.data?.advanced.limit} rimanenti oggi
                    </p>
                  )}

                  {searchResults && searchResults.length > 1 && (
                    <div className="bg-white border border-blue-200 rounded p-2 space-y-1 max-h-56 overflow-y-auto">
                      <p className="text-xs font-medium text-gray-700 mb-1">
                        Seleziona un risultato:
                      </p>
                      {searchResults.map((r) => (
                        <button
                          key={r.partita_iva}
                          type="button"
                          onClick={() => applySearchResult(r)}
                          className="w-full text-left p-2 hover:bg-blue-50 rounded text-sm border border-transparent hover:border-blue-300"
                        >
                          <div className="font-medium">{r.denominazione}</div>
                          <div className="text-xs text-gray-500">
                            P.IVA: {r.partita_iva}
                            {r.indirizzo?.citta &&
                              ` · ${r.indirizzo.citta}${r.indirizzo.provincia ? ` (${r.indirizzo.provincia})` : ''}`}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
<RequestOpenapiUpgradeModal isOpen={upgradeMode !== null} mode={upgradeMode ?? 'feature'} onClose={() => setUpgradeMode(null)} />
  </>;
}
